/*
 * Copyright (c) 2022. World Wide Generation Ltd
 */

import React, { Fragment, ReactNode } from 'react';
import { ScopeQuestionOptionalValue } from '@g17eco/types/survey';
import { SurveyQuestionListProps } from '../survey-question-list';
import { ColumnSelectbox } from './ColumnSelectbox';
import { LazyLoadingTrackingList } from './LazyLoadingTrackingList';
import { RowEmpty } from './RowEmpty';
import { BulkActionToolbarSelectedUtrv } from './BulkActionToolbar';
import { ColumnTitle } from '@g17eco/molecules/survey-question-list';
import { Divider, Row } from '@g17eco/molecules/tracking-list';
import { ColumnValue } from './ColumnValue';
import { ColumnStatusIcon } from './ColumnStatusIcon';
import { generateUrl } from '../../../routes/util';
import { ROUTES } from '../../../constants/routes';
import { useHistory, useLocation } from 'react-router-dom';
import { HighlightQuestion } from '../utils';
import { ColumnCommonProps, QuestionListColumn } from '@g17eco/types/survey-question-list';
import { ColumnBookmark } from './ColumnBookmark';

const defaultColumns: QuestionListColumn[] = [
  ColumnBookmark,
  ColumnTitle,
  Divider,
  ColumnValue,
  Divider,
  ColumnStatusIcon,
];

interface QuestionListProps
  extends Pick<SurveyQuestionListProps, 'columns' | 'goToQuestion' | 'initiativeId' | 'surveyId' | 'unitConfig' | 'submittedUtrvId'> {
  questionGroupId: string;
  questionList: ScopeQuestionOptionalValue[];
  startIndex: number;
  toggleSelectQuestion: (utrv: BulkActionToolbarSelectedUtrv) => void;
  enableBulkActions: boolean;
  colour?: string;
  // @deprecated in favour of preferredAltCodes
  alternativeCode?: string;
  preferredAltCodes?: string[];
  highlightQuestion?: HighlightQuestion;
  isQuestionSelected: (utrvId: string) => boolean;
  rowRef?: React.ForwardedRef<HTMLDivElement>;
  isBookmarked: (utrvId: string) => boolean;
  toggleBookmark: (utrvId: string) => void;
  renderExpansionRow?: (question: ScopeQuestionOptionalValue, rowIndex: number) => ReactNode;
  expandedRowId?: string;
  getDisabledRowTooltip?: (question: ScopeQuestionOptionalValue) => string | undefined;
}

const highlightClass = 'highlight-blink';

const removeClass: React.MouseEventHandler<HTMLDivElement> = (e) => e.currentTarget.classList.remove(highlightClass);

export const QuestionList = (props: QuestionListProps) => {
  const {
    questionGroupId,
    toggleSelectQuestion,
    enableBulkActions,
    colour,
    startIndex,
    alternativeCode,
    preferredAltCodes,
    questionList,
    initiativeId,
    surveyId,
    highlightQuestion,
    isQuestionSelected,
    rowRef,
    isBookmarked,
    toggleBookmark,
    renderExpansionRow = null,
    getDisabledRowTooltip = undefined,
    unitConfig,
    expandedRowId,
    submittedUtrvId,
  } = props;

  const location = useLocation();
  const history = useHistory();

  const columns = props.columns ?? defaultColumns;
  const style = colour ? { borderColor: colour } : {};

  const handleGoToQuestion = (id: string, questionIndex: number) => {
    if (props.goToQuestion) {
      return props.goToQuestion(id, questionIndex);
    }

    const surveyPath = generateUrl(ROUTES.COMPANY_TRACKER_SURVEY, {
      initiativeId,
      surveyId,
      page: 'question',
    });

    const url = `${surveyPath}/${id}/${questionIndex ?? ''}`;
    const searchParams = new URLSearchParams(location.search);

    history.push({
      pathname: url,
      search: searchParams.toString(),
    });
  };

  return (
    <LazyLoadingTrackingList
      className='questionList'
      style={style}
      forceShow={highlightQuestion?.groupId === questionGroupId}
    >
      {questionList.map((question, rowIndex) => {
        if (!question.utrv) {
          return (
            <RowEmpty
              key={`${questionGroupId}-empty-${rowIndex}`}
              question={question}
              rowIndex={rowIndex}
              alternativeCode={alternativeCode}
              preferredAltCodes={preferredAltCodes}
            />
          );
        }

        const questionIndex = startIndex + rowIndex;
        const isHighlighted = highlightQuestion?.questionIndex === questionIndex;
        const utrvId = question.utrv._id;
        const columnProps: ColumnCommonProps = {
          surveyId,
          initiativeId,
          question,
          alternativeCode,
          preferredAltCodes,
          handleGoToQuestion: () => handleGoToQuestion(utrvId, questionIndex),
          isBookmarked: isBookmarked(utrvId),
          toggleBookmark: () => toggleBookmark(utrvId),
          unitConfig,
        };

        const key = `${questionGroupId}-${utrvId}-${rowIndex}`;
        const ref = highlightQuestion?.questionIndex === questionIndex ? rowRef : undefined;
        const isExpanded = expandedRowId === question.utrv?._id;
        const isSubmittedUtrv = submittedUtrvId === utrvId;
        return (
          <Fragment key={key}>
            <Row
              ref={ref}
              tooltip={getDisabledRowTooltip?.(question)}
              onMouseEnter={removeClass}
              className={`clickable index-${questionIndex} ${question.utrv.status} ${
                isHighlighted ? highlightClass : ''
              } ${isExpanded ? 'expansion-title' : ''} ${isSubmittedUtrv ? 'is-submitted' : ''}`}
            >
              {columns.map((el, ix) => React.createElement(el, { ...columnProps, key: `col-${ix}-${key}` }))}
              {enableBulkActions ? (
                <ColumnSelectbox
                  {...columnProps}
                  isSelected={isQuestionSelected(utrvId)}
                  toggleSelectQuestion={toggleSelectQuestion}
                  disabled={getDisabledRowTooltip?.(question) !== undefined}
                />
              ) : null}
            </Row>
            {renderExpansionRow?.(question, rowIndex)}
          </Fragment>
        );
      })}
    </LazyLoadingTrackingList>
  );
};
